/**
 * Post preview popup (PRD "Post preview popup", §5.1 #28).
 *
 * Opens from the Posts table, the Feed and Top posts; prev/next step through the list that opened
 * it, in its current filtered and sorted order. The body is the LinkedIn-style render plus the
 * author's baselines, the metric history mini-chart when ≥2 snapshots exist (the shared kit
 * `Chart`, one line per metric), reactors, comments and the raw inspector.
 *
 * Everything else lives in the `Modal` footer, which `Modal` already pins outside the scrolling
 * body — one sticky bar: the metric row with its inline multipliers on top, then the same six
 * actions the feed card footer carries (five of them icon-only), the word/character counts and the
 * pager. "Copy link" hands out the post's **canonical**
 * deep link — its author's view screen with `?post=` (§12.1) — built by `postDeepLink`, so the
 * popup, a view feed and the global feed all copy one address per post rather than three.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AGGREGATE_METRIC_KEYS,
  type BaselineInfo,
  type MetricKey,
  type PostCommentDTO,
  type PostDetailDTO,
  type PostDetailResponse,
  type PostHistoryPointDTO,
  type PostReactorDTO,
} from '@lw/shared';
import { describeError, isNotFoundError } from '../../api/client';
import { getPost, getPostRaw } from '../../api/posts';
import { QK } from '../../api/queryKeys';
import {
  Avatar,
  BookmarkFilledIcon,
  BookmarkIcon,
  Chart,
  ChartLegend,
  CheckIcon,
  ChevronLeftIcon,
  CopyIcon,
  EmptyState,
  ErrorState,
  ExternalLinkIcon,
  IconAction,
  LinkIcon,
  Modal,
  NotionIcon,
  SparkleIcon,
  Spinner,
  type ChartSeries,
} from '../../components';
import { postDeepLink } from '../../app/routes';
import { useCommentChat } from '../ai/CommentChatProvider';
import { useNotionSave } from '../notion/useNotionSave';
import { useSaveDialog } from '../saved/SaveDialog';
import { SavedRowsBlock } from '../saved/SavedRowsBlock';
import { useCopyToClipboard } from '../../lib/hooks';
import { formatDecimal, formatInteger } from '../../lib/format';
import { AGGREGATE_METRIC_LABEL, METRIC_LABEL, WINDOW_KIND_LABEL } from '../../lib/labels';
import { formatDate } from '../../lib/datetime';
import {
  EditedChip,
  EmbeddedOriginalBlock,
  GroupLine,
  MetricsRow,
  PossiblyDeletedMarker,
  PostComponentBlock,
  PostMediaArea,
  PostText,
  PostTypeBadges,
  RepostedLine,
  UnmappedBadge,
} from './postRender';

const HISTORY_SERIES: { key: MetricKey; color: string }[] = [
  { key: 'reactions', color: 'var(--series-reactions)' },
  { key: 'comments', color: 'var(--series-comments)' },
  { key: 'shares', color: 'var(--series-shares)' },
  { key: 'saves', color: 'var(--series-saves)' },
];

/** Poll posts snapshot their vote total alongside the metrics — one more line, same chart. */
const POLL_SERIES = { key: 'pollVotes' as const, color: 'var(--series-poll, #a855f7)' };

function BaselinePanel({ baseline, timezone }: { baseline: BaselineInfo; timezone: string }) {
  return (
    <div className="baseline">
      <h4 className="preview__subtitle">
        Baseline — {baseline.authorName}
        {baseline.computedAt !== null ? (
          <span className="muted"> · computed {formatDate(baseline.computedAt, timezone)}</span>
        ) : null}
      </h4>
      <div className="card card--table">
        <div className="table-wrap">
          <table className="table table--sm">
            <thead>
              <tr>
                <th>Metric</th>
                <th className="num">Median</th>
                <th>Window</th>
                <th className="num">Sample</th>
              </tr>
            </thead>
            <tbody>
              {AGGREGATE_METRIC_KEYS.map((metric) => {
                const info = baseline.metrics[metric];
                return (
                  <tr key={metric}>
                    <td>{AGGREGATE_METRIC_LABEL[metric]}</td>
                    <td className="num">{formatDecimal(info.median)}</td>
                    <td className="cell-muted">{WINDOW_KIND_LABEL[info.windowKind]}</td>
                    <td className="num">{formatInteger(info.sampleCount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="faint">
        Multipliers always use the post author's own baseline — in group views too, they are never
        pooled.
      </p>
    </div>
  );
}

/** Snapshot-history lines; labels are the server's capture-date strings, values raw geometry. */
function HistoryChart({
  series,
  withPollVotes,
}: {
  series: PostHistoryPointDTO[];
  withPollVotes: boolean;
}) {
  const labels = series.map((point) => point.capturedAtDisplay);
  const lines: { key: MetricKey | 'pollVotes'; color: string; label: string }[] = [
    ...HISTORY_SERIES.map(({ key, color }) => ({ key, color, label: METRIC_LABEL[key] })),
    ...(withPollVotes ? [{ ...POLL_SERIES, label: 'Poll votes' }] : []),
  ];
  const chartSeries: ChartSeries[] = lines.map(({ key, color }) => ({
    key,
    type: 'line',
    color,
    values: series.map((point) => point[key]),
  }));

  return (
    <div className="preview__chart">
      <Chart
        labels={labels}
        series={chartSeries}
        height={200}
        ariaLabel="Metric history by capture date"
        renderTooltip={(index) => {
          const point = series[index];
          if (point === undefined) return null;
          return (
            <>
              <div className="t-label">{point.capturedAtDisplay}</div>
              {lines.map(({ key, label }) => (
                <div className="t-row" key={key}>
                  <span className="t-label">{label}</span>
                  <span className="t-value">{formatInteger(point[key])}</span>
                </div>
              ))}
            </>
          );
        }}
      />
      <ChartLegend items={lines.map(({ label, color }) => ({ label, color }))} />
    </div>
  );
}

/** Opt-in sub-scrape detail: the newest comments the last scrape returned (§ comments/reactions). */
function CommentsSection({ totalDisplay, items }: { totalDisplay: string; items: PostCommentDTO[] }) {
  if (items.length === 0) return null;
  return (
    <section className="preview__section">
      <h3 className="preview__title">Comments</h3>
      {/* Count and truncation note are one server-built string (§12.5). */}
      <p className="faint">{totalDisplay}</p>
      <ul className="comment-list">
        {items.map((comment) => (
          <li className="comment" key={comment.id}>
            <div className="comment__head">
              {comment.authorUrl !== null ? (
                <a href={comment.authorUrl} target="_blank" rel="noreferrer noopener">
                  {comment.authorName ?? 'Someone'}
                </a>
              ) : (
                <span>{comment.authorName ?? 'Someone'}</span>
              )}
              {comment.commentedAtDisplay !== null ? (
                <span className="faint">{comment.commentedAtDisplay}</span>
              ) : null}
              {comment.reactions !== null ? (
                <span className="muted">{comment.reactionsDisplay} reactions</span>
              ) : null}
            </div>
            {comment.text !== null ? <p className="comment__text">{comment.text}</p> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Per-person reactions, behind the aggregate breakdown the metric row already shows. */
function ReactorsList({ totalDisplay, items }: { totalDisplay: string; items: PostReactorDTO[] }) {
  if (items.length === 0) return null;
  return (
    <details className="reactors">
      {/* The stored total, not the length of this slice — the list is capped server-side. */}
      <summary>Who reacted ({totalDisplay})</summary>
      <ul className="reactors__list">
        {items.map((reactor) => (
          <li key={reactor.id}>
            {reactor.url !== null ? (
              <a href={reactor.url} target="_blank" rel="noreferrer noopener">
                {reactor.name ?? 'Someone'}
              </a>
            ) : (
              <span>{reactor.name ?? 'Someone'}</span>
            )}
            <span className="muted"> · {reactor.typeDisplay}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The raw inspector (SPEC § graceful degradation). `extras` is already in the #28 payload, so the
 * section costs nothing until it is opened; the provider item behind the post is a separate
 * on-demand call (#39) because finding it means scanning stored payloads.
 */
function RawDataSection({ detail, postId }: { detail: PostDetailResponse; postId: number }) {
  const [wantRaw, setWantRaw] = useState(false);
  const raw = useQuery({
    queryKey: QK.postRaw(postId),
    queryFn: ({ signal }) => getPostRaw(postId, signal),
    enabled: wantRaw,
    staleTime: Infinity,
  });

  return (
    <details className="raw-inspector">
      <summary>
        Raw data
        {detail.post.unmappedKeys.length > 0 ? (
          <span className="tag tag--warn">{detail.post.unmappedKeys.length} unmapped</span>
        ) : null}
      </summary>
      <p className="faint">
        Everything the mapper stored for this post, unmapped fields included. Nothing fetched is
        ever discarded — a future mapper version can still read it.
      </p>
      <pre className="raw-json">{JSON.stringify(detail.post.extras, null, 2)}</pre>

      <div className="preview__actions">
        <button
          type="button"
          className="table-action"
          onClick={() => setWantRaw(true)}
          disabled={wantRaw && raw.isFetching}
        >
          <span className="table-action__label">
            {raw.isFetching ? 'Loading…' : 'Load raw item'}
          </span>
        </button>
        {raw.data !== undefined ? (
          <span className="faint">
            from payload #{raw.data.payloadId} · {raw.data.fetchedAtDisplay}
          </span>
        ) : null}
      </div>

      {raw.isError ? (
        <div className="preview__actions">
          {/* Only a 404 means "we looked everywhere and it is not there" (#39 walks every payload
              of the profile); any other failure is a request problem and says so. */}
          <p className="faint">
            {isNotFoundError(raw.error)
              ? "No stored payload still holds this post's raw item — the payload file may have been removed from the raw store."
              : describeError(raw.error)}
          </p>
          <button
            type="button"
            className="table-action"
            onClick={() => void raw.refetch()}
            disabled={raw.isFetching}
          >
            <span className="table-action__label">{raw.isFetching ? 'Retrying…' : 'Retry'}</span>
          </button>
        </div>
      ) : null}
      {raw.data !== undefined ? (
        <pre className="raw-json">{JSON.stringify(raw.data.item, null, 2)}</pre>
      ) : null}
    </details>
  );
}

/**
 * Whether the snapshot-less state is *by design* (§7.4): a pure repost never carries own metrics,
 * and a quote post whose engagement id could not be trusted has none either. Any other post is
 * simply not scraped yet, and saying "a repost carries the original's engagement" there is wrong.
 */
function carriesNoOwnMetrics(post: PostDetailDTO): boolean {
  if (post.postType === 'repost') return true;
  if (post.postType !== 'repost_with_comment') return false;
  return (['reactions', 'comments', 'shares', 'saves', 'totalEngagement'] as const).every(
    (key) => post.metrics[key].value === null,
  );
}

export type PostPreviewDialogProps = {
  postId: number;
  /** The list that opened the popup, in its current order — powers prev/next. */
  ids: number[];
  mediaEnabled: boolean;
  timezone: string;
  onNavigate: (postId: number) => void;
  onClose: () => void;
};

export function PostPreviewDialog({
  postId,
  ids,
  mediaEnabled,
  timezone,
  onNavigate,
  onClose,
}: PostPreviewDialogProps) {
  const query = useQuery({
    queryKey: QK.post(postId),
    queryFn: ({ signal }) => getPost(postId, signal),
  });
  const [copied, copy] = useCopyToClipboard();
  // Its own instance so the two confirmations never flip together.
  const [linkCopied, copyLink] = useCopyToClipboard();
  // Shared with the feed card: one post in flight spins on both surfaces (SPEC § The Save action).
  const notion = useNotionSave();
  const saving = notion.isSaving(postId);
  // The same popup every card surface opens, stacked over this dialog (SPEC `saved-posts`).
  const { openSave } = useSaveDialog();
  // Stacks over this dialog, like the save popup does (SPEC `Comment Generation`).
  const { openCommentChat } = useCommentChat();

  // A post opened from outside the list that spawned the dialog (the embedded original of a
  // repost) has no position in it: the popup then stands alone rather than rendering a blank
  // position label between two permanently disabled buttons.
  const index = ids.indexOf(postId);
  const standalone = index < 0 || ids.length < 2;
  const previousId = index > 0 ? ids[index - 1] : undefined;
  const nextId = index >= 0 && index < ids.length - 1 ? ids[index + 1] : undefined;

  const detail = query.data;

  return (
    <Modal
      open
      size="lg"
      title="Post"
      onClose={onClose}
      className="post-preview"
      footer={
        detail === undefined ? null : (
          <>
            {/* The multipliers ride inline with their own metrics, so the Baselines section in the
                body is left with the medians they are measured against. */}
            <MetricsRow
              metrics={detail.post.metrics}
              multipliers={detail.performance.multipliers}
              withTotalMultiplier
            />
            <div className="preview__bar-actions">
              {/* The same six actions the feed card carries, in the same order. */}
              <div className="preview__bar-left">
                {detail.post.url !== null ? (
                  <IconAction
                    label="Open on LinkedIn"
                    href={detail.post.url}
                    icon={<ExternalLinkIcon size={14} />}
                    showLabel
                  />
                ) : null}
                <IconAction
                  label={detail.post.saved ? 'Saved' : 'Save'}
                  icon={
                    detail.post.saved ? <BookmarkFilledIcon size={14} /> : <BookmarkIcon size={14} />
                  }
                  onClick={() => openSave(postId)}
                />
                <IconAction
                  label={saving ? 'Saving to Notion…' : 'Save to Notion'}
                  icon={saving ? <Spinner size="sm" /> : <NotionIcon size={14} />}
                  disabled={saving}
                  onClick={() => {
                    notion.save(postId);
                  }}
                />
                <IconAction
                  label="Generate comment"
                  icon={<SparkleIcon size={14} />}
                  onClick={() => openCommentChat(postId)}
                />
                <IconAction
                  label={linkCopied ? 'Copied' : 'Copy link'}
                  icon={linkCopied ? <CheckIcon size={14} /> : <LinkIcon size={14} />}
                  onClick={() => copyLink(postDeepLink(postId, detail.author.id))}
                />
                <IconAction
                  label={copied ? 'Copied' : 'Copy text'}
                  icon={copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
                  disabled={detail.post.text === null}
                  onClick={() => copy(detail.post.text ?? '')}
                />
              </div>
              <span className="faint">
                {formatInteger(detail.post.words)} words · {formatInteger(detail.post.chars)}{' '}
                characters
              </span>
              {/* Standalone drops the pager only — stats, actions and counts always render, and
                  the grid's empty third cell keeps the counts centered. */}
              {standalone ? null : (
                <div className="preview__bar-right">
                  <IconAction
                    label="Previous post"
                    icon={<ChevronLeftIcon size={14} />}
                    disabled={previousId === undefined}
                    onClick={() => previousId !== undefined && onNavigate(previousId)}
                  />
                  <span className="preview__pos muted">{`${index + 1} / ${ids.length}`}</span>
                  <IconAction
                    label="Next post"
                    icon={<ChevronLeftIcon size={14} className="icon-flip-x" />}
                    disabled={nextId === undefined}
                    onClick={() => nextId !== undefined && onNavigate(nextId)}
                  />
                </div>
              )}
            </div>
          </>
        )
      }
    >
      {query.isPending ? <Spinner size="lg" label="Loading post…" /> : null}

      {query.isError ? (
        <ErrorState
          title="Couldn't load this post"
          error={query.error}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : null}

      {detail !== undefined ? (
        <article className="preview__body">
          <header className="post-card__head">
            <Avatar
              media={detail.author.avatar}
              name={detail.author.name}
              mediaEnabled={mediaEnabled}
              kind={detail.author.kind}
            />
            <div className="post-card__author">
              <a
                className="post-card__name"
                href={detail.author.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {detail.author.name ?? detail.author.slug}
              </a>
              {detail.author.headline !== null ? (
                <span className="muted post-card__headline">{detail.author.headline}</span>
              ) : null}
              <span className="faint">{detail.post.publishedAtDisplay}</span>
            </div>
            <div className="post-card__flags">
              <PostTypeBadges
                postType={detail.post.postType}
                contentType={detail.post.contentType}
                typeDisplay={detail.post.typeDisplay}
              />
              <EditedChip edited={detail.post.edited} />
              <PossiblyDeletedMarker possiblyDeleted={detail.post.possiblyDeleted} />
              <UnmappedBadge keys={detail.post.unmappedKeys} />
            </div>
          </header>

          {/* The same rows the cards carry, from the same server-built block (#28). */}
          {detail.post.savedInfo === null ? null : (
            <SavedRowsBlock info={detail.post.savedInfo} onEdit={() => openSave(postId)} />
          )}

          <RepostedLine postType={detail.post.postType} />
          <GroupLine group={detail.post.group} />
          <PostText text={detail.post.text} collapsible={false} />
          <PostMediaArea media={detail.media} mediaEnabled={mediaEnabled} />
          {/* Shape A (pure repost) renders its typed component inside the embedded frame — the
              video/document/poll belongs to the original, not to the repost. */}
          {detail.post.postType === 'repost' ? null : (
            <PostComponentBlock component={detail.post.component} mediaEnabled={mediaEnabled} />
          )}
          <EmbeddedOriginalBlock
            embedded={detail.post.embedded}
            mediaEnabled={mediaEnabled}
            onOpenPost={onNavigate}
            component={detail.post.postType === 'repost' ? detail.post.component : null}
          />

          <section className="preview__section">
            <h3 className="preview__title">Baselines</h3>
            <BaselinePanel baseline={detail.performance.baselineInfo} timezone={timezone} />
          </section>

          <section className="preview__section">
            <h3 className="preview__title">Metric history</h3>
            {detail.history.series.length === 0 ? (
              carriesNoOwnMetrics(detail.post) ? (
                <EmptyState
                  title="This post has no metrics of its own"
                  description="A repost carries the original's engagement, never its own, so nothing is snapshotted for it. The original's numbers appear on the embedded card above."
                />
              ) : (
                <EmptyState
                  title="No history yet"
                  description="Metrics are snapshotted on every scrape that covers this post; the first one has not landed yet."
                />
              )
            ) : detail.history.sufficient ? (
              <HistoryChart
                series={detail.history.series}
                withPollVotes={detail.history.hasPollVotes}
              />
            ) : (
              <EmptyState
                title="Only one snapshot so far"
                description="A metric history appears once this post has been captured by at least two scrapes — posts inside the re-scrape window accumulate a time series."
              />
            )}
          </section>

          <ReactorsList
            totalDisplay={detail.reactors.totalDisplay}
            items={detail.reactors.items}
          />
          <CommentsSection
            totalDisplay={detail.comments.totalDisplay}
            items={detail.comments.items}
          />

          <section className="preview__section">
            <RawDataSection detail={detail} postId={postId} />
          </section>
        </article>
      ) : null}
    </Modal>
  );
}

export default PostPreviewDialog;
